import React from "react";

import { Tune as TuneIcon } from "@mui/icons-material";
import {
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { usePrompt } from "hooks";
import { usePickUserPermissions } from "store";
import { usePortfolioConstructionActions } from "store/actions-context";
import { IPortfolioConstructionCorrelationData, UserPermissionType } from "store/types";

import { CorrelationSettingsPrompt } from "./CorrelationSettingsPrompt";

interface CorrelationMatrixProps {
  data: IPortfolioConstructionCorrelationData;
  investorId?: number;
  isFreemium?: boolean;
}

const getCorrelationColor = (value: number): string => {
  if (value === 1) return "#92A7BC"; // Blue-gray for perfect correlation (diagonal)
  if (value >= 0.5) return "#BFFFBE"; // Green for strong positive correlation
  if (value >= 0.25) return "#BFFFBE"; // Light green for moderate positive correlation
  if (value > 0) return "#EFFFEE"; // Very light green for weak positive correlation
  if (value === 0) return "#ffffff"; // White for no correlation
  if (value >= -0.25) return "#FFF0F0"; // Light pink for weak negative correlation
  if (value >= -0.5) return "#FFCBCC"; // Medium pink for moderate negative correlation
  return "#FFA6A7"; // Red for strong negative correlation
};

const formatLabel = (label: string): string => {
  // Remove "deal_name: " prefix if present
  return label.replace(/^deal_name:\s*/, "");
};

export const CorrelationMatrix: React.FC<CorrelationMatrixProps> = ({ data, investorId, isFreemium }) => {
  const { correlations, labels } = data;
  const openPrompt = usePrompt();
  const { setCorrelationMatrixSettingsAction } = usePortfolioConstructionActions();
  const featurePermission = usePickUserPermissions("portfolio_construction");
  const isViewerPermission = featurePermission === UserPermissionType.VIEWER;

  if (!correlations || !labels || correlations.length === 0) {
    return (
      <Stack sx={{ alignItems: "center", py: "40px" }}>
        <Typography sx={{ color: "#5d7782" }}>No correlation data available</Typography>
      </Stack>
    );
  }

  const onSettingsClick = async () => {
    const result = await openPrompt({
      Component: CorrelationSettingsPrompt,
      props: { investorId }
    });

    if (!result) return;
    setCorrelationMatrixSettingsAction(result);
  };

  return (
    <Stack
      sx={{
        width: "100%",
        gap: "20px",
        position: "relative",
        justifyContent: "flex-start",
        ...(isFreemium && { maxHeight: "329px" })
      }}
    >
      {!isViewerPermission && (
        <IconButton
          onClick={onSettingsClick}
          sx={{
            width: "32px",
            height: "32px",
            bgcolor: "#FFFFFF",
            border: "1px solid #7b92a9",
            borderRadius: "50%",
            position: "absolute",
            right: "calc(50% - 16px)",
            top: "-16px",
            zIndex: 10
          }}
        >
          <TuneIcon sx={{ fontSize: "16px", color: "#071f35" }} />
        </IconButton>
      )}
      <TableContainer
        sx={{
          maxHeight: "600px",
          maxWidth: "100%",
          overflow: "auto",
          display: "flex",
          justifyContent: "flex-start"
        }}
      >
        <Table stickyHeader size="small" sx={{ border: "0.5px solid #dbe8f5", minWidth: "max-content" }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  bgcolor: "#FFFFFF",
                  border: "1px solid #f6f8fc",
                  padding: "4px",
                  width: "80px",
                  height: "80px",
                  minWidth: "80px",
                  maxWidth: "80px",
                  fontWeight: 600,
                  color: "#0a3142",
                  position: "sticky",
                  left: 0,
                  zIndex: 5
                }}
              >
                {/* Empty corner cell */}
              </TableCell>
              {labels.map((label) => (
                <TableCell
                  key={label}
                  sx={{
                    bgcolor: "#FFFFFF",
                    border: "1px solid #f6f8fc",
                    padding: "4px",
                    width: "80px",
                    height: "80px",
                    minWidth: "80px",
                    maxWidth: "80px",
                    textAlign: "center",
                    fontWeight: 400,
                    color: "#0a3142",
                    fontSize: "12px",
                    lineHeight: "1.2",
                    verticalAlign: "middle"
                  }}
                >
                  {formatLabel(label)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {correlations.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                <TableCell
                  sx={{
                    bgcolor: "#FFFFFF",
                    border: "1px solid #f6f8fc",
                    padding: "4px",
                    width: "80px",
                    height: "80px",
                    minWidth: "80px",
                    maxWidth: "80px",
                    fontWeight: 400,
                    color: "#0a3142",
                    fontSize: "12px",
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    verticalAlign: "middle",
                    textAlign: "center"
                  }}
                >
                  {formatLabel(labels[rowIndex])}
                </TableCell>
                {row.map((value, colIndex) => (
                  <TableCell
                    key={colIndex}
                    sx={{
                      bgcolor: getCorrelationColor(value),
                      border: "1px solid #f6f8fc",
                      padding: "4px",
                      width: "80px",
                      height: "80px",
                      minWidth: "80px",
                      maxWidth: "80px",
                      textAlign: "center",
                      fontWeight: 500,
                      fontSize: "12px",
                      verticalAlign: "middle"
                    }}
                  >
                    {value.toFixed(2)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};
